
import type { QueryData } from '@empathybroker/tsclient-stats/dist/models/queries/query-data'
import type { Pagination, QueriesSearchData } from '../../../models'
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../../store/context/context.consts'
import PaginationBar from '../../crud/pagination-bar.component.vue'
import { EpbText } from '../../shared/epb-text'
import SearchBox from '../../shared/search-box.component.vue'
import ItemsList from './items-list.component.vue'

export default defineComponent({
  name: 'EpbSearch',
  components: {
    EpbText,
    SearchBox,
    ItemsList,
    PaginationBar,
  },
  props: {
    query: String,
  },
  computed: {
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data,
      selectedId: ANALYTICS_CONSTS.getters.selectedId,
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    globalQueryCount(): number {
      return this.searchQueriesData.reduce(
        (total: number, dataItem: QueryData) => total + (dataItem.queryCount || 0),
        0,
      )
    },
    searchQueriesData(): QueryData[] {
      return this.data.search[this.query]?.queries
    },
    results(): QueryData[] {
      if (!this.searchQueriesData) {
        return []
      }
      return this.searchQueriesData.map((searchDataItem: QueryData) => ({
        id: searchDataItem.query,
        count: searchDataItem.queryCount,
        percentage: (searchDataItem.queryCount / this.globalQueryCount) * 100,
      }))
    },
  },
  data(): Record<string, any> {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 5,
        totalItems: 0,
      },
      loading: false,
      messages: this.$epb.messages.insights.analytics.queries,
    }
  },
  methods: {
    ...mapActions(ANALYTICS_CONSTS.moduleName, {
      loadSearchData: ANALYTICS_CONSTS.actions.loadSearchData,
    }),
    async loadData(resetPagination = false): Promise<void> {
      if (resetPagination) {
        this.pagination.page = 1
      }
      this.loading = true
      try {
        const searchData: QueriesSearchData = await this.loadSearchData(this.pagination)
        this.pagination.totalItems = searchData ? searchData.totals.termsCount : 0
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loading = false
      }
    },
    onQueryChange(newValue): void {
      this.$emit('queryChange', newValue)
    },
    onPaginationChange(pagination: Pagination): void {
      this.pagination = pagination
      this.loadData()
    },
  },
  watch: {
    query(query: string): void {
      if (query) {
        this.loadData(true)
      }
    },
  },
  created(): void {
    if (this.query) {
      this.loadData()
    }
  },
})
